import { useMemo, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type ApiError, type GetHomeResult } from '@whinerp/client';

/** The checklist as the home payload carries it. */
type ChecklistData = Extract<GetHomeResult['checklist'], { ok: true }>['data'];
import {
  ActivityChip,
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  ErrorState,
  Figure,
  Funnel,
  Grid,
  Hero,
  Icon,
  InlineAction,
  MetricTile,
  Num,
  PageHeading,
  Row,
  Skeleton,
} from '@whinerp/ui';
import {
  dueStateForDate,
  formatCount,
  formatMoney,
  formatRelativeDate,
  partOfDay,
} from '@whinerp/contracts/format';
import { useFormat, useT } from '../../app/i18n.js';
import { useSession } from '../../app/session.js';
import { defineScreen } from '../types.js';

/**
 * SC-04 Home.
 *
 * Two columns on desktop, one below 1024px, My Day first on mobile - which is
 * why My Day is first in the source and the columns are a grid rather than two
 * hand-ordered halves.
 *
 * Every number here came from count() or sum() on the server. Not one is the
 * length of an array this screen received. The spec asks for "per-widget
 * error, never a whole-page failure", and each widget arrives as either its
 * data or the reason it has none, so that is a shape rather than a promise.
 */

type Home = GetHomeResult;

function HomeScreen(): JSX.Element {
  const t = useT();
  const format = useFormat();
  const { session } = useSession();

  const home = useQuery<Home, ApiError>({ queryKey: ['home'], queryFn: () => api.getHome({}) });

  const greeting = useMemo(() => {
    const key = {
      morning: 'work.greeting.morning',
      afternoon: 'work.greeting.afternoon',
      evening: 'work.greeting.evening',
    } as const;
    return t(key[partOfDay(new Date(), format)], {
      values: { name: session.user.name.split(/\s+/)[0] ?? session.user.name },
    });
  }, [t, format, session.user.name]);

  if (home.isPending) return <HomeLoading />;
  if (home.isError) return <HomeError error={home.error} retry={() => void home.refetch()} />;

  const data = home.data;

  return (
    <div data-component="Home" className="flex w-full flex-col gap-5">
      <PageHeading
        kicker={session.workspace.name}
        lede={t('home.lede')}
      >
        {greeting}
      </PageHeading>

      {/*
        * The hero: the one figure a person came for, and what it leaves out.
        *
        * The scope line is not decoration. This total is the workspace's own
        * currency only, so it says how many deals it covers out of how many
        * there are - a figure whose scope is invisible is not a smaller number
        * than it looks, it is a number the reader cannot size.
        */}
      <Hero>
        <Figure
          data-home-kpi
          label={t('home.kpi.openPipeline')}
          unit={data.kpi.currency}
          value={formatMoney(data.kpi.amount, format, { currency: data.kpi.currency, symbol: false })}
          scope={
            data.kpi.dealCount === 0
              ? t('home.kpi.noDeals')
              : data.kpi.countedInCurrency < data.kpi.dealCount
                ? t('home.kpi.someCurrencies', {
                    values: {
                      counted: formatCount(data.kpi.countedInCurrency, format),
                      count: formatCount(data.kpi.dealCount, format),
                    },
                  })
                : t('home.kpi.dealCount', {
                    values: { count: formatCount(data.kpi.dealCount, format) },
                  })
          }
        />
        {/*
          * Weighted, beside it rather than instead of it.
          *
          * One says what is in play and the other what a forecast would carry.
          * Both are drawn over exactly the same deals, which is what makes the
          * difference between them mean anything.
          */}
        <Figure
          data-home-weighted
          className="ms-auto text-end"
          size="secondary"
          label={t('home.kpi.weighted')}
          unit={data.kpi.currency}
          value={formatMoney(data.kpi.weighted, format, { currency: data.kpi.currency, symbol: false })}
          scope={t('home.kpi.weighted.basis')}
        />
      </Hero>

      {/*
        * The strip, 2a's four-up row of secondary figures.
        *
        * Every one is a count() the server already returned and the screen had
        * nowhere to put - the object counts were reaching the browser and
        * being drawn as a list of links, which is a navigation control wearing
        * a number rather than a figure somebody can read across.
        */}
      {data.lists.length === 0 ? null : (
        <Grid columns="quad" data-home-strip>
          {data.lists.slice(0, 4).map((list) => (
            <Card key={list.objectKey} data-home-count={list.objectKey} data-list-widget={list.objectKey}>
              <div className="px-5 pb-5 pt-4">
                <MetricTile
                  align="start"
                  label={list.label}
                  value={formatCount(list.count, format)}
                  href={list.href}
                  labels={{
                    up: (change) => t('metric.trend.up', { values: { change } }),
                    down: (change) => t('metric.trend.down', { values: { change } }),
                    flat: t('metric.trend.flat'),
                  }}
                />
              </div>
            </Card>
          ))}
        </Grid>
      )}

      <Grid columns="main-aside">
        {/* My Day first in source order: on mobile the grid collapses and this
            is what a rep needs before anything else. */}
        <Widget
          title={t('home.myDay')}
          widget={data.myDay}
          labels={{ failed: t('home.widget.failed') }}
          flush
          action={
            <a href="/work" className="text-bronze-text hover:underline underline-offset-4">
              {t('home.myDay.openWork')}
            </a>
          }
        >
          {(myDay) =>
            myDay.steps.length === 0 ? (
              <p className="px-5 pb-5 text-body text-ink-meta">{t('home.myDay.empty')}</p>
            ) : (
              <div>
                {myDay.steps.map((step) => {
                  const overdue = dueStateForDate(step.dueDate, format) === 'overdue';
                  return (
                    /*
                      * Declared columns, so name, kind, timing and action line
                      * up down the card. Read as four lists rather than eight
                      * separate rows, which is the difference between scanning
                      * a day and reading it.
                      */
                    <Row key={step.id} columns="step" data-work-step={step.id}>
                      {step.recordName ? (
                        <a
                          href={step.recordHref ?? undefined}
                          className="truncate text-body text-ink hover:text-bronze-text"
                        >
                          {step.recordName}
                        </a>
                      ) : (
                        <span className="truncate text-body text-ink">{step.typeLabel}</span>
                      )}
                      {/*
                        * The chip carries what kind of step and when, and says
                        * "nothing scheduled" in words when there is none - a
                        * blank there is indistinguishable from a step that
                        * failed to load. Overdue is a colour AND a word.
                        */}
                      <ActivityChip
                        compact
                        step={{
                          typeLabel: step.typeLabel,
                          typeIcon: step.typeIcon,
                          dueLabel: formatRelativeDate(`${step.dueDate}T12:00:00Z`, format),
                          overdue,
                        }}
                        labels={{
                          nothingScheduled: t('work.complete.noNext'),
                          schedule: t('work.complete.nextStep'),
                          overdue: t('work.section.overdue'),
                        }}
                      />
                      {/*
                        * Opens the record. NOT a one-click Complete: completing
                        * a step takes an outcome, a note and what happens next,
                        * which is a dialog on My work - and a control that
                        * completed without asking would record an outcome
                        * nobody chose.
                        */}
                      <a
                        href={step.recordHref ?? '/work'}
                        className="text-end text-dense text-bronze-text opacity-0 transition-opacity duration-state group-hover:opacity-100 focus-visible:opacity-100"
                      >
                        {t('home.myDay.open')}
                      </a>
                    </Row>
                  );
                })}
              </div>
            )
          }
        </Widget>

        <Widget title={t('home.pipeline')} widget={data.pipeline} labels={{ failed: t('home.widget.failed') }}>
          {(pipeline) =>
            pipeline.stages.length === 0 ? (
              <p className="text-body text-ink-meta">{t('home.pipeline.empty')}</p>
            ) : (
              <div className="flex flex-col gap-2">
                {/*
                  * Which pipeline these figures are about.
                  *
                  * The widget summarises the default pipeline, and said so
                  * nowhere - fine while a workspace had one, and a total whose
                  * scope is invisible as soon as it has two. The figure is
                  * right; a reader had no way to know what it excluded.
                  */}
                <p data-pipeline-scope className="text-micro uppercase tracking-label text-ink-label">
                  {pipeline.pipelineLabel}
                </p>
                <Num className="text-dense text-ink-meta">
                  {t('home.pipeline.open', {
                    values: {
                      count: formatCount(pipeline.openCount, format),
                      amount: formatMoney(pipeline.openTotal, format, { currency: pipeline.currency }),
                    },
                  })}
                </Num>
                {/*
                  * Rails rather than a table.
                  *
                  * Each is drawn against the largest stage, never as a share
                  * of a total - the stages of a pipeline do not add to one,
                  * and a bar that implied they did would be a different claim
                  * from the one the numbers make. Both figures stay written
                  * out: a stage with no deals shows zero beside a zero total,
                  * which is what tells "empty" apart from "worthless".
                  */}
                <Funnel
                  rows={pipeline.stages.map((stage) => ({
                    id: stage.stageId,
                    label: stage.label,
                    weight: Number(stage.total),
                    value: formatMoney(stage.total, format, { currency: pipeline.currency }),
                    aside: formatCount(stage.count, format),
                    settled: stage.outcome !== 'open',
                    attrs: { 'data-stage': stage.stageId },
                  }))}
                />
                {/*
                  * What the totals cover, at the foot of the figures rather
                  * than nowhere. These are one currency only, and a reader who
                  * cannot see that reads a smaller number as the whole.
                  */}
                <p data-pipeline-covers className="pt-3 text-dense text-ink-meta">
                  {t('home.pipeline.covers', {
                    values: {
                      counted: formatCount(pipeline.countedInCurrency, format),
                      count: formatCount(pipeline.openCount, format),
                      currency: pipeline.currency,
                    },
                  })}
                </p>
                <a href="/deals" className="text-dense text-bronze-text underline underline-offset-4">
                  {t('home.pipeline.board')}
                </a>
              </div>
            )
          }
        </Widget>

        <Widget title={t('home.recent')} widget={data.recent} labels={{ failed: t('home.widget.failed') }}>
          {(recent) =>
            recent.length === 0 ? (
              <p className="text-body text-ink-meta">{t('home.recent.empty')}</p>
            ) : (
              <ul className="flex flex-col">
                {recent.map((entry) => (
                  <li
                    key={`${entry.objectKey}-${entry.recordId}`}
                    className="flex min-h-touch items-center gap-3 border-t border-t-rule py-2 first:border-t-0"
                  >
                    <a href={entry.href} className="flex-1 truncate text-body text-ink hover:text-bronze-text">
                      {entry.name}
                    </a>
                    <Num className="text-meta text-ink-meta">
                      {formatRelativeDate(entry.updatedAt, format)}
                    </Num>
                  </li>
                ))}
              </ul>
            )
          }
        </Widget>

        <Widget
          title={t('home.checklist')}
          widget={data.checklist}
          labels={{ failed: t('home.widget.failed') }}
        >
          {(checklist) => <Checklist checklist={checklist} />}
        </Widget>

      </Grid>
    </div>
  );
}

/**
 * The getting-started list, which a person can put away.
 *
 * Two different controls and they are not the same thing. COLLAPSE is a
 * per-viewer convenience that lasts until they leave; DISMISS is a decision
 * the server remembers, per person, because two people in one workspace are at
 * different points in setting it up.
 *
 * Dismissing never removes the way back. A control that hides the only
 * guidance on the screen forever is one people are afraid to press, so the
 * dismissed state is a line of text and a link that brings it back - not
 * nothing.
 */
function Checklist({ checklist }: { checklist: ChecklistData }): JSX.Element {
  const t = useT();
  const format = useFormat();
  const client = useQueryClient();
  const [collapsed, setCollapsed] = useState(false);

  const dismiss = useMutation({
    mutationFn: (dismissed: boolean) => api.dismissChecklist({ body: { dismissed } }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['home'] });
    },
  });

  if (checklist.dismissed) {
    return (
      <div className="flex flex-col gap-2" data-checklist-dismissed>
        <p className="text-meta text-ink-meta">{t('home.checklist.hidden')}</p>
        <InlineAction data-checklist-restore onClick={() => dismiss.mutate(false)}>
          {t('home.checklist.show')}
        </InlineAction>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2" data-checklist>
      <div className="flex flex-wrap items-center gap-2">
        <Num className="text-meta text-ink-meta" data-checklist-progress>
          {t('home.checklist.done', {
            values: {
              done: formatCount(checklist.done, format),
              total: formatCount(checklist.total, format),
            },
          })}
        </Num>
        <span className="ms-auto flex gap-3">
          <InlineAction
            data-checklist-collapse
            aria-expanded={!collapsed}
            onClick={() => setCollapsed((was) => !was)}
          >
            {t(collapsed ? 'home.checklist.expand' : 'home.checklist.collapse')}
          </InlineAction>
          <InlineAction data-checklist-dismiss onClick={() => dismiss.mutate(true)}>
            {t('home.checklist.hide')}
          </InlineAction>
        </span>
      </div>
      {collapsed ? null : (
        <ul className="flex flex-col" data-checklist-items>
          {checklist.items.map((item) => (
            <li
              key={item.key}
              data-checklist-item={item.key}
              data-checklist-done={item.done}
              className="flex min-h-touch items-center gap-2 py-1"
            >
              <span className={item.done ? 'text-success-text' : 'text-ink-300'}>
                <Icon name={item.done ? 'CheckCircle' : 'Circle'} size="inline" decorative />
              </span>
              {item.done ? (
                <span className="text-body text-ink-meta line-through">{item.label}</span>
              ) : (
                <a
                  href={item.href}
                  className="text-body text-bronze-text underline underline-offset-4"
                >
                  {item.label}
                </a>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * One widget, which either has its data or says why it has none.
 *
 * A widget that failed renders its own message and its own retry. A whole-page
 * error because the pipeline summary timed out would take My Day down with it,
 * and My Day is the reason the person opened the screen.
 */
function Widget<T>({
  title,
  widget,
  labels,
  action,
  flush = false,
  children,
}: {
  title: string;
  widget: { ok: true; data: T } | { ok: false; reason: string };
  labels: { failed: string };
  /** A link at the end of the header - "see all of these". */
  action?: ReactNode;
  /** Rows run to the card's edge, so their hairlines reach it. */
  flush?: boolean;
  children: (data: T) => ReactNode;
}): JSX.Element {
  return (
    <Card data-widget={title}>
      <CardHeader title={title} action={action} />
      <CardBody flush={flush}>
        {widget.ok ? (
          children(widget.data)
        ) : (
          <p
            role="status"
            data-widget-failed
            className="flex items-start gap-2 text-dense text-warning-text"
          >
            <Icon name="Warning" size="inline" decorative />
            <span>
              {labels.failed}
              {/* The server's own sentence, not a generic apology. */}
              <span className="block text-ink-meta">{widget.reason}</span>
            </span>
          </p>
        )}
      </CardBody>
    </Card>
  );
}

function HomeEmpty(): JSX.Element {
  const t = useT();
  return (
    <EmptyState
      reason="no-records"
      illustration="House"
      title={t('home.checklist')}
      body={t('home.checklist.complete')}
      primary={{ label: t('home.myDay.all'), href: '/work', icon: 'ListChecks' }}
    />
  );
}

function HomeLoading(): JSX.Element {
  const t = useT();
  return (
    <div className="flex w-full flex-col gap-5" role="status" aria-live="polite" aria-busy>
      <span className="sr-only">{t('state.loading')}</span>
      <Skeleton className="h-line-h1 w-block-xl" />
      {/* Skeleton cards preserving the final layout, as SC-04 asks: two
          columns on desktop, so the page does not jump when it arrives. */}
      <Grid columns="main-aside">
        {Array.from({ length: 4 }, (_, index) => (
          <Skeleton key={index} className="h-block-lg w-full" />
        ))}
      </Grid>
    </div>
  );
}

function HomeError({ error, retry }: { error: unknown; retry: () => void }): JSX.Element {
  return (
    <ErrorState
      title="Home could not be loaded"
      body={error instanceof Error ? error.message : 'The page did not load.'}
      onRetry={retry}
    />
  );
}

export const screen = defineScreen({
  id: 'SC-04',
  route: '/',
  surface: 'page',
  title: 'Home',
  /*
   * F-024 is delivered here and on every other screen, which is what makes it
   * awkward to declare. Its controls live in the account menu - the "Profile"
   * the specification names, which is chrome rather than a screen - and its
   * effect is every date, count and amount a person reads. Home is where they
   * first meet all three, one click from the menu that changes them.
   *
   * F-022 is declared here for the same reason and with the same discomfort:
   * the bell and its panel are shell, present on every screen, and Home is
   * where somebody lands and reads the count for the first time.
   */
  features: ['F-150', 'F-024', 'F-022'],
  entitlement: 'F-150',
  reachedBy: [{ kind: 'nav', item: 'home' }],
  components: ['MetricTile', 'ActivityChip'],
  elements: ['Home'],
  states: {
    empty: HomeEmpty,
    loading: HomeLoading,
    error: HomeError,
  },
  component: HomeScreen,
});

export default screen;
